/**
 * Money formatting and parsing utilities for currency display and input
 *
 * TODO: Add user preference toggle for negative format: -$1,234.56 vs ($1,234.56)
 * This should be implemented as a user profile setting in the future.
 */

/**
 * Formats a numeric value with comma separators
 * @param {number|string|null|undefined} value - The numeric value to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeSign - Whether to include + sign for positive numbers
 * @returns {string} Formatted number with commas (e.g., "1,234.56")
 */
export const formatMoney = (value, options = {}) => {
  const { includeSign = false } = options;

  // Handle null, undefined, or empty string
  if (value === null || value === undefined || value === '') {
    return '0.00';
  }

  // Parse to number
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // Handle NaN
  if (isNaN(numValue)) {
    return '0.00';
  }

  // Format with commas and exactly 2 decimal places
  const formatted = Math.abs(numValue).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // Handle sign
  if (numValue < 0) {
    return `-${formatted}`;
  } else if (includeSign && numValue > 0) {
    return `+${formatted}`;
  }

  return formatted;
};

/**
 * Formats a numeric value as currency with $ sign
 * @param {number|string|null|undefined} value - The numeric value to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeSign - Whether to include + sign for positive numbers
 * @returns {string} Formatted currency (e.g., "$1,234.56" or "-$1,234.56")
 */
export const formatCurrency = (value, options = {}) => {
  // Handle null, undefined, or empty string
  if (value === null || value === undefined || value === '') {
    return '$0.00';
  }

  // Parse to number
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // Handle NaN
  if (isNaN(numValue)) {
    return '$0.00';
  }

  const formatted = formatMoney(Math.abs(numValue), { includeSign: false });

  // Handle negative values: -$1,234.56
  if (numValue < 0) {
    return `-$${formatted}`;
  } else if (options.includeSign && numValue > 0) {
    return `+$${formatted}`;
  }

  return `$${formatted}`;
};

/**
 * Parses a money string to a float, removing commas and other formatting
 * @param {string|number|null|undefined} value - The value to parse
 * @returns {number|null} Parsed float value or null if invalid
 */
export const parseMoney = (value) => {
  // Handle null, undefined, or empty string
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // If already a number, return it
  if (typeof value === 'number') {
    return value;
  }

  // Strip commas and whitespace from string
  const cleaned = value.toString().replace(/,/g, '').trim();

  // Return null for empty string after cleaning
  if (cleaned === '') {
    return null;
  }

  // Parse to float
  const parsed = parseFloat(cleaned);

  // Return null if NaN
  return isNaN(parsed) ? null : parsed;
};

/**
 * Validates that input is a valid money format
 * Allows: digits, decimal point, minus sign, commas
 * @param {string} value - The input string to validate
 * @returns {boolean} True if valid money input format
 */
export const isValidMoneyInput = (value) => {
  // Empty string is valid (allows clearing)
  if (value === '') {
    return true;
  }

  // Allow digits, decimal point, minus sign (at start), and commas
  // Only one decimal point allowed
  // Minus sign only at the beginning
  const moneyRegex = /^-?\d{1,3}(,?\d{3})*(\.\d{0,2})?$/;
  const simpleRegex = /^-?\d*\.?\d{0,2}$/;

  return moneyRegex.test(value) || simpleRegex.test(value);
};

/**
 * Strips commas from a string (useful for equation parsing)
 * @param {string} value - The string to clean
 * @returns {string} String with commas removed
 */
export const stripCommas = (value) => {
  if (typeof value !== 'string') {
    return value;
  }
  return value.replace(/,/g, '');
};
